import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, X, ArrowRight, ArrowLeft, Sparkles, Check, Building2, Sun, Clock, Euro, PenLine, ListChecks, SkipForward, AlertTriangle, Pencil } from "lucide-react";
import SignatureCapture from "../../../components/SignatureCapture";
import { TextField, LogoUploader } from "./shared";
import { SeasonsEditor, TiersEditor, RateGrid } from "./PricingSection";

const STEPS = [
  { key: "company", label: "Entreprise", icon: Building2, title: "Votre entreprise", desc: "Ces informations apparaissent sur vos devis, contrats et factures." },
  { key: "seasons", label: "Saisons", icon: Sun, title: "Vos saisons tarifaires", desc: "Basse, moyenne, haute… définissez les périodes qui font varier vos prix." },
  { key: "tiers", label: "Durées", icon: Clock, title: "Vos paliers de durée", desc: "Le tarif journalier devient dégressif selon la durée de location." },
  { key: "rates", label: "Tarifs", icon: Euro, title: "Votre grille de prix", desc: "Prix €/jour par catégorie, saison et durée. C'est la source unique des tarifs de location." },
  { key: "signature", label: "Signature", icon: PenLine, title: "Signature & tampon", desc: "Apposés automatiquement sur vos contrats — vous n'aurez plus rien à signer manuellement." },
  { key: "recap", label: "Récap", icon: ListChecks, title: "Récapitulatif", desc: "Un dernier coup d'œil avant de terminer. Cliquez sur une ligne pour la compléter." },
];

export default function SetupWizard({ settings, onClose, onFinished }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ completed: [], skipped: [] });
  const [company, setCompany] = useState({ ...settings.company });
  const [pricing, setPricing] = useState(JSON.parse(JSON.stringify(settings.pricing)));
  const [signature, setSignature] = useState(null);
  const [stamp, setStamp] = useState(null);

  useEffect(() => {
    api.get("/vendor/profile/signature")
      .then(({ data }) => { setSignature(data.signature_data_url || null); setStamp(data.stamp_data_url || null); })
      .catch(() => {});
  }, []);

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const setC = (k, v) => setCompany((p) => ({ ...p, [k]: v }));
  const setP = (k, v) => setPricing((p) => ({ ...p, [k]: v }));

  const saveStep = async (key) => {
    if (key === "company") await api.put("/vendor/settings", { company });
    if (["seasons", "tiers", "rates"].includes(key)) await api.put("/vendor/settings", { pricing: { ...pricing } });
    if (key === "signature") await api.put("/vendor/profile/signature", { signature_data_url: signature ?? "", stamp_data_url: stamp ?? "" });
  };

  const next = async (skip = false) => {
    setSaving(true);
    try {
      if (!skip) await saveStep(current.key);
      const completed = skip ? progress.completed : [...new Set([...progress.completed, current.key])];
      const skipped = skip ? [...new Set([...progress.skipped, current.key])] : progress.skipped.filter((k) => k !== current.key);
      setProgress({ completed, skipped });
      if (isLast) {
        await api.put("/vendor/settings", { setup: { completed: true, completed_steps: completed.filter((k) => k !== "recap"), skipped_steps: skipped } });
        toast.success("Configuration terminée — votre espace est prêt 🎉");
        onFinished();
      } else {
        setStep(step + 1);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur d'enregistrement");
    }
    setSaving(false);
  };

  const categories = settings.fleet?.categories || [];

  const ratedCats = categories.filter((c) => Object.values(pricing.rates?.[c.value] || {}).some((season) => Object.values(season || {}).some((p) => Number(p) > 0)));
  const recapItems = [
    { key: "company", label: "Entreprise", icon: Building2, value: company.name ? `${company.name}${company.registration_number ? " · SIRET renseigné" : ""}` : "Non renseignée", ok: !!company.name },
    { key: "seasons", label: "Saisons", icon: Sun, value: `${(pricing.seasons || []).length} saison${(pricing.seasons || []).length > 1 ? "s" : ""} (${(pricing.seasons || []).map((s) => s.label).join(", ")})`, ok: (pricing.seasons || []).length > 0 },
    { key: "tiers", label: "Durées", icon: Clock, value: `${(pricing.duration_tiers || []).length} palier${(pricing.duration_tiers || []).length > 1 ? "s" : ""} dégressif${(pricing.duration_tiers || []).length > 1 ? "s" : ""}`, ok: (pricing.duration_tiers || []).length > 0 },
    { key: "rates", label: "Tarifs", icon: Euro, value: ratedCats.length > 0 ? `${ratedCats.length} catégorie${ratedCats.length > 1 ? "s" : ""} tarifée${ratedCats.length > 1 ? "s" : ""} (${ratedCats.map((c) => c.label).join(", ")})` : "Aucun prix défini", ok: ratedCats.length > 0 },
    { key: "signature", label: "Signature", icon: PenLine, value: signature ? `Enregistrée${stamp ? " + tampon" : ""}` : "Manquante", ok: !!signature },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6" data-testid="setup-wizard">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[92vh] flex flex-col rounded-3xl bg-white shadow-2xl overflow-hidden" style={{ animation: "jnMaterialize 0.35s both" }}>
        {/* Header */}
        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-violet-50/70 to-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold tracking-wider uppercase">
                <Sparkles className="h-3 w-3" /> Configuration guidée
              </div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900 mt-2">{current.title}</h2>
              <p className="text-sm text-slate-500 mt-1">{current.desc}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0" data-testid="wizard-close" aria-label="Fermer">
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Steps indicator — cliquables */}
          <div className="flex items-center gap-1.5 mt-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const state = i < step ? "done" : i === step ? "active" : "todo";
              return (
                <React.Fragment key={s.key}>
                  {i > 0 && <div className={`h-px flex-1 ${i <= step ? "bg-emerald-400" : "bg-slate-200"}`} />}
                  <button
                    type="button"
                    onClick={() => setStep(i)}
                    className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold transition-all cursor-pointer ${
                      state === "active"
                        ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/40 scale-105 ring-2 ring-emerald-300"
                        : state === "done"
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        : "bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                    }`}
                    data-testid={`wizard-step-${s.key}`}
                    aria-label={`Aller à l'étape ${s.label}`}
                  >
                    {state === "active" && <span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ping" />}
                    {state === "done" ? <Check className="relative h-3 w-3" /> : <Icon className="relative h-3 w-3" />}
                    <span className="relative hidden sm:inline">{s.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
          {current.key === "company" && (
            <div className="space-y-5">
              <LogoUploader value={company.logo_data_url} onChange={(v) => setC("logo_data_url", v)} testid="wizard-logo" />
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Nom de l'entreprise" value={company.name} onChange={(v) => setC("name", v)} placeholder="Paris Luxury Cars" testid="wizard-company-name" />
                <TextField label="N° d'enregistrement (SIRET)" value={company.registration_number} onChange={(v) => setC("registration_number", v)} placeholder="123 456 789 00012" testid="wizard-company-registration" />
              </div>
              <TextField label="Adresse du siège" value={company.address} onChange={(v) => setC("address", v)} placeholder="12 rue de la Paix, 75002 Paris" testid="wizard-company-address" />
              <TextField label="Téléphone" value={company.phone} onChange={(v) => setC("phone", v)} placeholder="01 23 45 67 89" testid="wizard-company-phone" />
            </div>
          )}
          {current.key === "seasons" && <SeasonsEditor seasons={pricing.seasons} onChange={(v) => setP("seasons", v)} testid="wizard-seasons" />}
          {current.key === "tiers" && <TiersEditor tiers={pricing.duration_tiers} onChange={(v) => setP("duration_tiers", v)} testid="wizard-tiers" />}
          {current.key === "rates" && (
            <RateGrid categories={categories} seasons={pricing.seasons} tiers={pricing.duration_tiers} rates={pricing.rates} onChange={(v) => setP("rates", v)} testid="wizard-rates" />
          )}
          {current.key === "signature" && (
            <div className="space-y-6">
              <SignatureCapture label="Ma signature" value={signature} onChange={setSignature} testid="wizard-signature" />
              <SignatureCapture label="Mon tampon (optionnel)" value={stamp} onChange={setStamp} height={120} testid="wizard-stamp" />
            </div>
          )}
          {current.key === "recap" && (
            <div className="space-y-2" data-testid="wizard-recap">
              {recapItems.map((item) => {
                const skipped = progress.skipped.includes(item.key) && !item.ok;
                const idx = STEPS.findIndex((s) => s.key === item.key);
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setStep(idx)}
                    className={`group w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all hover:-translate-y-px hover:shadow-sm ${item.ok ? "border-emerald-200 bg-emerald-50/50 hover:border-emerald-300" : "border-amber-200 bg-amber-50/50 hover:border-amber-300"}`}
                    data-testid={`wizard-recap-${item.key}`}
                  >
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${item.ok ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}`}>
                      <item.icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{item.label}</div>
                      <div className={`text-xs truncate ${item.ok ? "text-slate-500" : "text-amber-700 font-medium"}`}>{item.value}</div>
                    </div>
                    {item.ok ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold shrink-0"><Check className="h-3 w-3" /> Configuré</span>
                    ) : skipped ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold shrink-0"><SkipForward className="h-3 w-3" /> Sauté</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold shrink-0"><AlertTriangle className="h-3 w-3" /> À compléter</span>
                    )}
                    <Pencil className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
                  </button>
                );
              })}
              {recapItems.every((i) => i.ok) ? (
                <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-800 flex items-center gap-2">
                  <Check className="h-4 w-4 shrink-0" /> Tout est prêt — votre espace Junago est entièrement configuré.
                </div>
              ) : (
                <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 shrink-0 text-violet-500" /> Vous pourrez compléter les éléments manquants à tout moment depuis les Paramétrages.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} disabled={saving} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-40" data-testid="wizard-back">
                <ArrowLeft className="h-3.5 w-3.5" /> Retour
              </button>
            )}
            {!isLast && (
              <button onClick={() => next(true)} disabled={saving} className="text-xs font-semibold text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-40" data-testid="wizard-skip">
                Passer cette étape
              </button>
            )}
          </div>
          <button
            onClick={() => next(false)}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors"
            data-testid="wizard-next"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isLast ? <Check className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
            {isLast ? "Terminer la configuration" : "Enregistrer & continuer"}
          </button>
        </div>
      </div>
    </div>
  );
}
